// `useCreateIssue` — Phase 2d SSE create path. Replaces Phase 2c's
// `useCreateIssueRaw` for the new-issue flow. The PATCH path stays on
// `useUpdateIssue`.
//
// The hook owns one in-flight SSE connection; `submit` opens it, the
// returned state surfaces the phase + step the server is currently in,
// and `result` populates on the final `done` event. The component
// renders progress copy from `phase` and switches to `result` when set.

import { useCallback, useRef, useState } from "react";

import {
  IntakeResponseSchema,
  type AcceptedLink,
  type IntakeProgressEvent,
  type IntakeProgressPhase,
  type IntakeResponse,
  type IssueEditorPayload,
} from "@loom/shared/dashboard";

import { UnauthorizedError, signalUnauthorized } from "../lib/http";

export interface CreateIssueProgress {
  readonly phase: IntakeProgressPhase;
  readonly step: string;
  readonly message?: string;
}

export interface CreateIssueState {
  readonly submit: (
    payload: IssueEditorPayload,
    accepted: ReadonlyArray<AcceptedLink>,
    /**
     * Idempotency key (sent as `X-Idempotency-Key`). When the same key is
     * resent — a reload-retry after the server already created the issue — the
     * server returns the existing issue instead of filing a duplicate. A header
     * (not a body field) so a client/server deploy skew can't break the strict
     * request envelope. Omit to disable dedupe (legacy behaviour).
     */
    idempotencyKey?: string,
  ) => Promise<IntakeResponse>;
  readonly saving: boolean;
  readonly progress: CreateIssueProgress | null;
  readonly result: IntakeResponse | null;
  readonly error: string | null;
}

const parseSseEvent = (chunk: string): { event: string; data: string } | null => {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of chunk.split("\n")) {
    if (line.startsWith(":")) continue;
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
};

export const useCreateIssue = (projectId: number): CreateIssueState => {
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<CreateIssueProgress | null>(null);
  const [result, setResult] = useState<IntakeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef(false);

  const submit = useCallback(
    async (
      payload: IssueEditorPayload,
      accepted: ReadonlyArray<AcceptedLink>,
      idempotencyKey?: string,
    ): Promise<IntakeResponse> => {
      if (inflight.current) throw new Error("create already in flight");
      inflight.current = true;
      setSaving(true);
      setError(null);
      setResult(null);
      setProgress(null);
      try {
        const res = await fetch(`/api/projects/${projectId}/issues`, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "content-type": "application/json",
            accept: "text/event-stream",
            ...(idempotencyKey
              ? { "x-idempotency-key": idempotencyKey }
              : {}),
          },
          body: JSON.stringify({ payload, accepted }),
        });
        if (res.status === 401) {
          // Same as `useDecompose`: an SSE fetch outside `jsonOrThrow`, so the
          // signal is explicit or there is none.
          signalUnauthorized(
            `/api/projects/${projectId}/issues`,
            await res.text().catch(() => ""),
          );
          throw new UnauthorizedError();
        }
        if (!res.ok || !res.body) {
          const detail = await res.text().catch(() => "");
          throw new Error(`${res.status} ${res.statusText}${detail ? `: ${detail}` : ""}`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let final: IntakeResponse | null = null;
        let streamErr: string | null = null;
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let sep = buffer.indexOf("\n\n");
          while (sep >= 0) {
            const chunk = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            sep = buffer.indexOf("\n\n");
            const parsed = parseSseEvent(chunk);
            if (!parsed) continue;
            if (parsed.event === "progress") {
              try {
                const evt = JSON.parse(parsed.data) as IntakeProgressEvent;
                setProgress({
                  phase: evt.phase,
                  step: evt.step,
                  ...(evt.message !== undefined ? { message: evt.message } : {}),
                });
              } catch {
                // ignore malformed progress frames
              }
            } else if (parsed.event === "done") {
              const data: unknown = JSON.parse(parsed.data);
              final = IntakeResponseSchema.parse(data);
            } else if (parsed.event === "error") {
              try {
                const errData = JSON.parse(parsed.data) as { message?: string };
                streamErr = errData.message ?? "stream_error";
              } catch {
                streamErr = "stream_error";
              }
            }
          }
        }
        if (streamErr !== null) throw new Error(streamErr);
        if (final === null) throw new Error("stream_ended_without_done");
        setResult(final);
        return final;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      } finally {
        inflight.current = false;
        setSaving(false);
      }
    },
    [projectId],
  );

  return { submit, saving, progress, result, error };
};
